import { defineComponent, ref, watch, nextTick } from 'vue';

export default defineComponent({
  name: 'MaskedInput',
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    mask: {
      type: [Array, Function, Object],
      required: true
    },
    placeholderChar: {
      type: String,
      default: '_'
    },
    keepCharPositions: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'update:modelValue'],
  setup(props, { emit }) {
    const inputRef = ref(null);
    const inputValue = ref(props.value?.toString() || '');

    watch(() => props.value, (newVal) => {
      const stringVal = newVal?.toString() || '';
      if (stringVal !== inputValue.value) {
        inputValue.value = stringVal;
        applyMask();
      }
    });

    const applyMask = async () => {
      await nextTick();
      if (!inputRef.value) return;

      const maskedValue = applyMaskToValue(inputValue.value, props.mask);
      if (maskedValue !== inputValue.value) {
        inputValue.value = maskedValue;
        inputRef.value.value = maskedValue;
      }
    };

    const handleInput = (event) => {
      const rawValue = event.target.value;
      const maskedValue = applyMaskToValue(rawValue, props.mask);
      
      inputValue.value = maskedValue;
      event.target.value = maskedValue;
      
      // Position cursor before suffix for number masks
      if (typeof props.mask === 'object' && props.mask.suffix) {
        const suffixLength = props.mask.suffix.length;
        const newCursorPosition = Math.max(0, maskedValue.length - suffixLength);
        nextTick(() => {
          if (inputRef.value) {
            inputRef.value.setSelectionRange(newCursorPosition, newCursorPosition);
          }
        });
      }
      
      emit('input', maskedValue);
      emit('update:modelValue', maskedValue);
    };

    const handleKeydown = (event) => {
      if (typeof props.mask === 'object' && props.mask.suffix && event.key === 'Backspace') {
        const cursorPosition = inputRef.value.selectionStart;
        const suffixLength = props.mask.suffix.length;
        const valueWithoutSuffix = inputRef.value.value.substring(0, inputRef.value.value.length - suffixLength);
        
        // If cursor is at or after the suffix, move it before the suffix
        if (cursorPosition >= valueWithoutSuffix.length) {
          event.preventDefault();
          
          // If there's content to delete, remove the last character
          if (valueWithoutSuffix.length > 0) {
            const newValue = valueWithoutSuffix.substring(0, valueWithoutSuffix.length - 1) + props.mask.suffix;
            inputRef.value.value = newValue;
            inputValue.value = newValue;
            
            // Position cursor at the end of the remaining number part
            const newPosition = Math.max(0, newValue.length - suffixLength);
            nextTick(() => {
              if (inputRef.value) {
                inputRef.value.setSelectionRange(newPosition, newPosition);
              }
            });
            
            emit('input', newValue);
            emit('update:modelValue', newValue);
          }
        }
      }
    };

    const applyMaskToValue = (value, maskConfig) => {
      if (!value || !maskConfig) return value;

      // Handle createNumberMask functions (from text-mask-addons)
      if (typeof maskConfig === 'function') {
        try {
          // Try to extract config from the function if possible
          if (maskConfig.instanceOptions) {
            return applyNumberMask(value, maskConfig.instanceOptions);
          }
          
          // Fallback: try to determine config from function behavior
          const testResult = maskConfig('1');
          if (Array.isArray(testResult)) {
            const hasPercent = testResult.some(item => item === '%');
            const hasDollar = testResult.some(item => item === '$');
            
            if (hasPercent) {
              return applyNumberMask(value, {
                prefix: '',
                suffix: '%',
                allowDecimal: true,
                decimalLimit: 2,
                allowNegative: false
              });
            } else if (hasDollar) {
              return applyNumberMask(value, {
                prefix: '$',
                suffix: '',
                allowDecimal: true,
                includeThousandsSeparator: true,
                thousandsSeparatorSymbol: ',',
                allowNegative: false
              });
            }
          }
          
          return value;
        } catch (error) {
          console.error('Error applying mask function:', error);
          return value;
        }
      }
      
      // Handle createNumberMask objects
      if (typeof maskConfig === 'object' && (maskConfig.prefix !== undefined || maskConfig.suffix !== undefined)) {
        return applyNumberMask(value, maskConfig);
      }
      
      // Handle string masks
      if (typeof maskConfig === 'string') {
        return applyStringMask(value, maskConfig);
      }

      return value;
    };

    const applyStringMask = (value, mask) => {
      const cleanValue = value.replace(/[^\w]/g, '');
      let maskedValue = '';
      let valueIndex = 0;
      
      for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
        if (mask[i] === '#') {
          maskedValue += cleanValue[valueIndex];
          valueIndex++;
        } else {
          maskedValue += mask[i];
        }
      }
      
      return maskedValue;
    };

    const applyNumberMask = (value, maskConfig) => {
      if (!value || value === '') return '';
      
      // Clean the value - remove everything except digits and decimal point
      let cleanValue = value.replace(/[^\d.]/g, '');
      
      // Handle multiple decimal points - keep only the first one
      const decimalIndex = cleanValue.indexOf('.');
      if (decimalIndex !== -1) {
        cleanValue = cleanValue.substring(0, decimalIndex + 1) + cleanValue.substring(decimalIndex + 1).replace(/\./g, '');
      }
      
      // Handle negative values
      if (!maskConfig.allowNegative && cleanValue.startsWith('-')) {
        cleanValue = cleanValue.substring(1);
      }
      
      // If it's just a decimal point, return empty or handle specially
      if (cleanValue === '.') {
        return maskConfig.prefix || '';
      }
      
      // Parse as number
      let numericValue = parseFloat(cleanValue);
      if (isNaN(numericValue)) {
        // If we have a partial decimal input like "4.", handle it
        if (cleanValue.endsWith('.') && cleanValue.length > 1) {
          numericValue = parseFloat(cleanValue.substring(0, cleanValue.length - 1));
          if (!isNaN(numericValue)) {
            // Check integer limit
            if (maskConfig.integerLimit && Math.floor(numericValue).toString().length > maskConfig.integerLimit) {
              const maxInteger = Math.pow(10, maskConfig.integerLimit) - 1;
              numericValue = Math.min(numericValue, maxInteger);
            }
            let result = (maskConfig.prefix || '') + numericValue.toString();
            if (maskConfig.allowDecimal !== false) {
              result += (maskConfig.decimalSymbol || '.');
            }
            result += (maskConfig.suffix || '');
            return result;
          }
        }
        return '';
      }
      
      // Apply negative constraint
      if (!maskConfig.allowNegative && numericValue < 0) {
        numericValue = Math.abs(numericValue);
      }
      
      // Apply integer limit constraint
      if (maskConfig.integerLimit) {
        const maxInteger = Math.pow(10, maskConfig.integerLimit) - 1;
        if (Math.floor(numericValue) > maxInteger) {
          numericValue = maxInteger + (numericValue % 1); // Keep decimal part, limit integer part
        }
      }
      
      let formattedValue = '';
      
      // Add prefix
      if (maskConfig.prefix) {
        formattedValue += maskConfig.prefix;
      }
      
      // Handle decimal formatting
      let numberStr;
      if (maskConfig.allowDecimal !== false && maskConfig.decimalLimit !== undefined) {
        // Don't automatically add trailing zeros for intermediate input
        if (cleanValue.includes('.')) {
          const decimalPart = cleanValue.split('.')[1] || '';
          const limitedDecimalPart = decimalPart.substring(0, maskConfig.decimalLimit);
          const integerPart = Math.floor(numericValue).toString();
          numberStr = integerPart + (limitedDecimalPart.length > 0 ? '.' + limitedDecimalPart : (cleanValue.endsWith('.') ? '.' : ''));
        } else {
          numberStr = Math.floor(numericValue).toString();
        }
      } else if (maskConfig.allowDecimal === false) {
        numberStr = Math.floor(numericValue).toString();
      } else {
        numberStr = numericValue.toString();
      }
      
      // Add thousands separator if enabled
      if (maskConfig.includeThousandsSeparator) {
        const parts = numberStr.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, maskConfig.thousandsSeparatorSymbol || ',');
        numberStr = parts.join(maskConfig.decimalSymbol || '.');
      }
      
      formattedValue += numberStr;
      
      // Add suffix
      if (maskConfig.suffix) {
        formattedValue += maskConfig.suffix;
      }
      
      return formattedValue;
    };

    return {
      inputRef,
      inputValue,
      handleInput,
      handleKeydown
    };
  },
  template: `
    <input
      ref="inputRef"
      :value="inputValue"
      @input="handleInput"
      @keydown="handleKeydown"
      v-bind="$attrs"
    />
  `
});
